'use client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import Icon from './Icons/Icon';
import { ToastProps } from '@/types/types';

const variantStyles = {
  info: {
    bg: 'bg-[#F0FAFF]',
    border: 'border-[#6FA9FF]',
    icon: <Icon name="InfoCircle" width={28} height={28} />,
  },
  success: {
    bg: 'bg-[#F7FFF0]',
    border: 'border-[#4CAF50]',
    icon: (
      <FontAwesomeIcon
        icon={['far', 'check-square']}
        className="text-[27px] text-[#4CAF50]"
      />
    ),
  },
  warning: {
    bg: 'bg-[#FFF9F0]',
    border: 'border-[#F4AC3F]',
    icon: <Icon name="ExclamationTriangle" width={31} height={27} />,
  },
  danger: {
    bg: 'bg-[#FFF0F0]',
    border: 'border-[#F43F5E]',
    icon: <Icon name="ExclamationTriangleDanger" width={31} height={27} />,
  },
};

const Toast: React.FC<ToastProps> = ({
  title,
  text,
  variant = 'warning',
  className,
  iconClassName,
  textClassName,
  duration = 5,
  position = 'bottom-right',
  onClose,
}) => {
  const [visible, setVisible] = useState(true);
  const styles = variantStyles[variant];

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false);
      onClose?.();
    }, duration * 1000);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  return (
    <div
      className={clsx(
        'p-5 max-w-[400px] min-w-[300px] flex gap-4 rounded-lg border-1 z-50 fixed',
        styles.bg,
        styles.border,
        className,
        position === 'top-right' && 'right-4 top-4',
        position === 'top-left' && 'left-4 top-4',
        position === 'bottom-left' && 'left-4 bottom-4',
        position === 'bottom-right' && 'right-4 bottom-4',

        position === 'top-right' || position === 'bottom-right'
          ? 'animate-slideInRight'
          : 'animate-slideInLeft',
        {
          [position === 'top-right' || position === 'bottom-right'
            ? '!animate-returnSlideInRight'
            : '!animate-returnSlideInLeft']: !visible,
        }
      )}
    >
      <div className={clsx('flex items-center', iconClassName)}>
        {styles.icon}
      </div>
      <div className="w-full">
        {title && (
          <p className="text-dark text-[17px] font-semibold leading-6 mb-1">
            {title}
          </p>
        )}
        <p
          className={clsx(
            'text-gray-medium text-[15px] leading-6 w-full',
            textClassName
          )}
        >
          {text}
        </p>
      </div>
      <span
        onClick={() => {
          setVisible(false);
          onClose?.();
        }}
      >
        <FontAwesomeIcon
          icon={['far', 'circle-xmark']}
          className="text-gray-medium cursor-pointer hover:text-dark"
        />
      </span>
    </div>
  );
};

export default Toast;
